/**
 * PipMomentsBridge — imperative escape hatch for the Pip governor.
 *
 * Spec: .plan/features/2026-06-10-pip-mascot-placement.md §3 + Rev 3.3.
 *
 * WHY THIS EXISTS
 * ----------------
 * The governor's `fire` is a React hook (usePipMoments) and can only be called
 * from a component rendered INSIDE <PipMomentsProvider>. Some placement triggers
 * live in handlers defined in a component that *mounts* the provider in its JSX
 * but is itself OUTSIDE the provider's subtree — e.g. `handleCreateRecipe` in
 * routes/app.tsx is declared on WorkspaceContainer, which renders
 * <PipMomentsProvider> as a child. That handler cannot call usePipMoments().
 *
 * This bridge mounts as a child of the provider, reads `fire` via the hook, and
 * writes it into a ref the parent holds — the same imperative-escape-hatch
 * idiom already used in routes/app.tsx (exportsOpenerRef, capsuleAccountRef).
 * The parent's handler then calls `pipFireRef.current?.("first-recipe")`.
 *
 * It renders NOTHING and adds no governor logic — it only re-exposes the
 * already-built `fire` (the governor still owns frequency + the collision rule).
 * Placements that ARE inside the provider subtree (LibraryGridView, Workspace,
 * NewVersionSheet) call usePipMoments() directly and do NOT need this bridge.
 */

import { useLayoutEffect, type MutableRefObject } from "react";
import { usePipMoments } from "./usePipMoments";
import type { PipMomentKey } from "./pipMoments";

/** The narrowed `fire` signature the bridge publishes to the parent ref. */
export type PipFire = (key: PipMomentKey) => void;

export interface PipMomentsBridgeProps {
  /**
   * A ref the parent owns. The bridge populates `.current` with the governor's
   * `fire` on mount (and clears it on unmount). The parent reads
   * `fireRef.current?.(key)` from handlers that sit outside the provider subtree.
   *
   * Null-current before mount is expected and handled by the parent's optional
   * chaining — a fire before the bridge mounts is a no-op (the governor is not
   * yet reachable), which is acceptable: the create flow that drives the first
   * fire only runs after the authenticated shell (and this bridge) has mounted.
   */
  fireRef: MutableRefObject<PipFire | null>;
}

export function PipMomentsBridge({ fireRef }: PipMomentsBridgeProps) {
  const { fire } = usePipMoments();

  useLayoutEffect(() => {
    fireRef.current = fire;
    return () => {
      // Clear only if we still own the slot (defensive against a re-mount race).
      if (fireRef.current === fire) {
        fireRef.current = null;
      }
    };
  }, [fire, fireRef]);

  return null;
}
